import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { AdditiveBlending, Color, type ShaderMaterial } from 'three';
import { palette } from '@/theme/palette';

/**
 * The kavşak itself — just a thin glowing ring sitting on the
 * platform at the centre, marking the point where all five star
 * roads meet. No brand mark, no spinning rings: deliberately
 * minimal so the highways themselves carry the visual weight.
 *
 * A faint additive disc beneath the ring fills it with a soft halo
 * so the junction reads from above as well as from the side.
 */
const DISC_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const DISC_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform float uTime;
  uniform vec3 uColor;
  varying vec2 vUv;
  void main() {
    // Distance from centre, normalised
    vec2 c = vUv - 0.5;
    float r = length(c) * 2.0;
    // Subtle radial breathing, but mostly a soft glow
    float pulse = 0.55 + 0.12 * sin(uTime * 0.9);
    float a = (1.0 - smoothstep(0.0, 1.0, r)) * pulse * 0.55;
    if (a < 0.005) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

export function Hub() {
  const matRef = useRef<ShaderMaterial>(null);
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uColor: { value: new Color(palette.cyan) },
    }),
    [],
  );

  useFrame((state) => {
    uniforms.uTime.value = state.clock.elapsedTime;
  });

  return (
    <group position={[0, 0, 0]}>
      {/* Soft additive halo on the ground */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]}>
        <circleGeometry args={[2.4, 64]} />
        <shaderMaterial
          ref={matRef}
          vertexShader={DISC_VERTEX}
          fragmentShader={DISC_FRAGMENT}
          uniforms={uniforms}
          transparent
          blending={AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Thin emissive ring marking the junction edge */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, 0]}>
        <ringGeometry args={[1.85, 1.95, 96]} />
        <meshBasicMaterial color={palette.cyan} toneMapped={false} />
      </mesh>
    </group>
  );
}

/**
 * Brand mark inside the junction — rendered OUTSIDE the rotating world
 * group so it stays readable (never spins with the kavşak). Use this
 * as a sibling of Hub, not a child of `worldRef`.
 */
export function HubBrand() {
  return (
    <Text
      position={[0, 0.02, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      fontSize={0.55}
      color={palette.cyan}
      anchorX="center"
      anchorY="middle"
      letterSpacing={0.02}
      outlineWidth={0.012}
      outlineColor="#02061f"
      outlineOpacity={0.6}
    >
      Pinorion
    </Text>
  );
}
